<template>
  <div class="benchmark-page">
    <!-- Sidebar — only shown once evaluation is complete -->
    <aside v-if="store.evaluationStatus === 'complete'" class="bench-sidebar">
      <nav class="bench-nav">
        <a
          href="#perf"
          class="bench-nav-item"
          :class="{ active: activeSection === 'perf' }"
          @click.prevent="scrollToSection('perf')"
          >Performance</a
        >
        <a
          href="#quality"
          class="bench-nav-item"
          :class="{ active: activeSection === 'quality' }"
          @click.prevent="scrollToSection('quality')"
          >AI Quality</a
        >
        <a
          href="#human-eval"
          class="bench-nav-item"
          :class="{ active: activeSection === 'human-eval' }"
          @click.prevent="scrollToSection('human-eval')"
          >Your Evaluation</a
        >
        <div class="bench-nav-group-label">Full Outputs</div>
        <a
          v-for="outputName in uniqueOutputNames"
          :key="outputName"
          :href="`#output-${outputName}`"
          class="bench-nav-item bench-nav-child"
          :class="{ active: activeSection === `output-${outputName}` }"
          @click.prevent="scrollToSection(`output-${outputName}`)"
          >{{ formatOutputName(outputName) }}</a
        >
        <a
          href="#recommendation"
          class="bench-nav-item"
          :class="{ active: activeSection === 'recommendation' }"
          @click.prevent="scrollToSection('recommendation')"
          >Recommendation</a
        >
      </nav>
    </aside>

    <div class="benchmark-content">
      <!-- Header -->
      <div class="benchmark-header">
        <div>
          <h1 class="benchmark-title">
            {{ store.title ? `${store.title} — Benchmark` : 'Benchmark' }}
          </h1>
          <p class="benchmark-subtitle">{{ store.modelRuns.length }} models · {{ frameworkLabel }} framework</p>
        </div>
        <button
          v-if="!store.allModelsComplete || store.evaluationStatus !== 'complete'"
          class="cancel-btn"
          @click="cancelRun"
        >
          Cancel
        </button>
      </div>

      <!-- Phase 1: Processing progress -->
      <section v-if="!store.allModelsComplete || store.evaluationStatus !== 'complete'" class="benchmark-section">
        <h2 class="section-heading">Running Models</h2>
        <div class="progress-grid">
          <div v-for="run in store.modelRuns" :key="run.projectId" class="progress-card" :class="run.status">
            <div class="progress-card-header">
              <span class="model-status-icon">
                <span v-if="run.status === 'complete'">✓</span>
                <span v-else-if="run.status === 'error'">✕</span>
                <span v-else class="spinner"></span>
              </span>
              <span class="model-name">{{ run.modelName }}</span>
            </div>
            <div class="progress-card-body">
              <div v-if="run.status === 'processing' || run.status === 'complete'">
                <div class="progress-bar-track">
                  <div
                    class="progress-bar-fill"
                    :style="{ width: run.totalSteps ? `${(run.stepCount / run.totalSteps) * 100}%` : '0%' }"
                  ></div>
                </div>
                <p class="progress-step-label">
                  <span v-if="run.status === 'complete'">Done</span>
                  <span v-else>{{ run.currentStep || 'Starting…' }}</span>
                </p>
              </div>
              <p v-if="run.status === 'error'" class="progress-error">{{ run.error }}</p>
              <p v-if="run.status === 'pending'" class="progress-pending">Waiting to start…</p>
            </div>
            <div class="progress-card-footer">
              <span class="elapsed">{{ elapsed(run) }}</span>
              <span v-if="run.costs" class="cost">${{ run.costs.cost.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Evaluation phase -->
        <div v-if="store.allModelsComplete" class="evaluation-status">
          <div
            v-if="store.evaluationStatus === 'creating' || store.evaluationStatus === 'processing'"
            class="eval-running"
          >
            <span class="spinner"></span>
            <span>Evaluating outputs…</span>
          </div>
          <div v-else-if="store.evaluationStatus === 'error'" class="eval-error">
            Evaluation failed: {{ store.evaluationError }}
          </div>
        </div>
      </section>

      <!-- Phase 2: Results -->
      <template v-if="store.evaluationStatus === 'complete'">
        <!-- Performance table -->
        <section id="perf" class="benchmark-section">
          <h2 class="section-heading">Performance</h2>
          <div class="table-wrapper">
            <table class="perf-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Cost</th>
                  <th>Duration</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Cached</th>
                  <th>Cache Savings</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="run in store.modelRuns" :key="run.projectId">
                  <td class="model-col">{{ run.modelName }}</td>
                  <td>${{ run.costs?.cost.toFixed(2) ?? '—' }}</td>
                  <td>{{ formatDuration(run.costs?.durationMs) }}</td>
                  <td>{{ formatNumber(run.costs?.inputTokens) }}</td>
                  <td>{{ formatNumber(run.costs?.outputTokens) }}</td>
                  <td>{{ formatNumber(run.costs?.cachedTokens) }}</td>
                  <td>{{ cacheSavings(run.costs) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Quality scores -->
        <section id="quality" class="benchmark-section">
          <h2 class="section-heading">AI Quality Evaluation</h2>
          <p class="section-note">Scores 1–10 per output type. Click any cell to see reasoning.</p>

          <div v-for="{ label, scores, outputKey } in evaluationSections" :key="outputKey" class="scores-block">
            <h3 class="scores-heading">{{ label }}</h3>
            <div
              v-if="scores"
              class="scores-grid"
              :style="{ gridTemplateColumns: `repeat(${store.modelRuns.length}, 1fr)` }"
            >
              <div
                v-for="run in store.modelRuns"
                :key="run.projectId"
                class="score-cell"
                :class="scoreClass(scores.scores[run.modelId]?.['overall'])"
                @click="toggleReasoning(outputKey + run.modelId)"
              >
                <div class="score-number">{{ scores.scores[run.modelId]?.['overall'] ?? '—' }}</div>
                <div class="score-model-label">{{ run.modelName }}</div>
                <div v-if="expandedReasonings.has(outputKey + run.modelId)" class="score-reasoning">
                  <div class="score-dimensions">
                    <div v-for="(val, dim) in scores.scores[run.modelId]" :key="dim" class="score-dim-row">
                      <span v-if="dim !== 'overall'" class="dim-name">{{ dim }}</span>
                      <span v-if="dim !== 'overall'" class="dim-val" :class="scoreClass(val as number)">{{ val }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p v-else class="scores-na">Not applicable for this framework.</p>
            <p v-if="scores" class="scores-summary">{{ scores.summary }}</p>
          </div>
        </section>

        <!-- Human ratings -->
        <section id="human-eval" class="benchmark-section">
          <h2 class="section-heading">Your Evaluation</h2>
          <p class="section-note">Rate each model's outputs. Saved automatically.</p>

          <div v-for="run in store.modelRuns" :key="run.projectId" class="human-rating-block">
            <h3 class="human-rating-model">{{ run.modelName }}</h3>
            <div v-for="{ label, outputKey } in evaluationSections" :key="outputKey" class="human-rating-row">
              <span class="human-rating-label">{{ label }}</span>
              <div class="star-rating">
                <button
                  v-for="n in 5"
                  :key="n"
                  class="star-btn"
                  :class="{ filled: (store.humanRatings[run.modelId + '_' + outputKey]?.rating ?? 0) >= n }"
                  @click="
                    store.saveHumanRating(
                      run.modelId + '_' + outputKey,
                      n,
                      store.humanRatings[run.modelId + '_' + outputKey]?.notes ?? ''
                    )
                  "
                >
                  ★
                </button>
              </div>
              <textarea
                class="rating-notes"
                placeholder="Notes…"
                :value="store.humanRatings[run.modelId + '_' + outputKey]?.notes ?? ''"
                @input="
                  e =>
                    store.saveHumanRating(
                      run.modelId + '_' + outputKey,
                      store.humanRatings[run.modelId + '_' + outputKey]?.rating ?? 0,
                      (e.target as HTMLTextAreaElement).value
                    )
                "
              ></textarea>
            </div>
          </div>
          <button class="export-btn" @click="store.exportHumanRatings()">Export ratings as JSON</button>
        </section>

        <!-- Model output tabs — one section per output type -->
        <div>
          <h2 class="section-heading outputs-heading">Full Outputs</h2>
          <div v-if="uniqueOutputNames.length === 0" class="output-empty">No outputs available.</div>
          <section
            v-for="outputName in uniqueOutputNames"
            :key="outputName"
            :id="`output-${outputName}`"
            class="benchmark-section output-block"
          >
            <h3 class="output-block-title">{{ formatOutputName(outputName) }}</h3>
            <div class="tab-bar">
              <button
                v-for="run in store.modelRuns"
                :key="run.projectId"
                class="tab-btn"
                :class="{ active: activeTabForOutput(outputName) === run.projectId }"
                @click="setActiveTabForOutput(outputName, run.projectId)"
              >
                {{ run.modelName }}
              </button>
            </div>
            <div
              v-for="run in store.modelRuns"
              :key="run.projectId"
              v-show="activeTabForOutput(outputName) === run.projectId"
            >
              <div
                class="prose-content"
                v-html="renderMarkdown(getOutputContent(outputName, run.projectId) ?? '')"
              ></div>
            </div>
          </section>
        </div>

        <!-- Overall recommendation -->
        <section id="recommendation" class="benchmark-section">
          <h2 class="section-heading">Recommendation</h2>
          <div v-if="store.overallRanking" class="ranking-summary">
            <div class="recommended-badge">
              Recommended:
              {{ BENCHMARK_MODEL_NAMES[store.overallRanking.recommended] ?? store.overallRanking.recommended }}
            </div>
            <div v-if="store.overallRanking.avoid" class="avoid-badge">
              Avoid: {{ BENCHMARK_MODEL_NAMES[store.overallRanking.avoid] ?? store.overallRanking.avoid }}
            </div>
          </div>
          <div class="prose-content" v-html="renderMarkdown(recommendationProse)"></div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { marked } from 'marked';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { BENCHMARK_MODEL_NAMES, useBenchmarkStore } from '../stores/benchmark';

marked.setOptions({ gfm: true, breaks: true });

const route = useRoute();
const store = useBenchmarkStore();

const activeOutputTabs = ref<Record<string, string>>({});
const expandedReasonings = ref(new Set<string>());
const now = ref(Date.now());
const activeSection = ref('perf');

// Tick elapsed timers every second while processing
let ticker: ReturnType<typeof setInterval> | null = null;
let observer: IntersectionObserver | null = null;

function setupObserver() {
  if (observer) observer.disconnect();
  observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          activeSection.value = entry.target.id;
        }
      }
    },
    { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
  );
  const ids = ['perf', 'quality', 'human-eval', 'recommendation', ...uniqueOutputNames.value.map(n => `output-${n}`)];
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  }
}

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  activeSection.value = id;
}

onMounted(async () => {
  const projectIds = ((route.query['projectIds'] as string | undefined) ?? '').split(',').filter(Boolean);
  const models = ((route.query['models'] as string | undefined) ?? '').split(',').filter(Boolean);
  const frameworkId = (route.query['framework'] as string | undefined) ?? 'novel';
  const manuscriptTitle = (route.query['title'] as string | undefined) ?? '';

  if (projectIds.length === 0) return;

  await store.init({ projectIds, modelIds: models, frameworkId, manuscriptTitle });

  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

// Set up observer once evaluation is complete and DOM is updated
watch(
  () => store.evaluationStatus,
  async status => {
    if (status === 'complete') {
      if (ticker) {
        clearInterval(ticker);
        ticker = null;
      }
      await new Promise(r => setTimeout(r, 100)); // wait for DOM
      setupObserver();
    }
  }
);

onBeforeUnmount(() => {
  store.cleanup();
  if (ticker) clearInterval(ticker);
  if (observer) observer.disconnect();
});

function cancelRun() {
  store.cleanup();
  window.location.href = '/';
}

const frameworkLabel = computed(() => {
  const names: Record<string, string> = {
    novel: 'Novel',
    'short-story': 'Short Story',
    memoir: 'Memoir',
    'non-fiction': 'Non-Fiction',
    roast: 'Roast',
  };
  return names[store.framework] ?? store.framework;
});

const evaluationSections = computed(() => [
  { label: 'Editorial Letter', outputKey: 'editorial', scores: store.editorialScores },
  { label: 'Chapter Notes', outputKey: 'chapter', scores: store.chapterScores },
  { label: 'Revision Plan', outputKey: 'revision', scores: store.revisionScores },
  { label: 'Welcome Letter', outputKey: 'welcome', scores: store.welcomeScores },
]);

const uniqueOutputNames = computed(() => {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const run of store.modelRuns) {
    for (const output of run.outputs) {
      if (!seen.has(output.name)) {
        seen.add(output.name);
        names.push(output.name);
      }
    }
  }
  return names;
});

function activeTabForOutput(outputName: string): string {
  if (!activeOutputTabs.value[outputName]) {
    const firstRun = store.modelRuns[0];
    return firstRun?.projectId ?? '';
  }
  return activeOutputTabs.value[outputName];
}

function setActiveTabForOutput(outputName: string, projectId: string) {
  activeOutputTabs.value = { ...activeOutputTabs.value, [outputName]: projectId };
}

function getOutputContent(outputName: string, projectId: string): string | undefined {
  const run = store.modelRuns.find(r => r.projectId === projectId);
  return run?.outputs.find(o => o.name === outputName)?.content;
}

// Strip the JSON block from the recommendation output, leaving only prose
const recommendationProse = computed(() => {
  const content = store.evaluationOutputs['benchmark_overall_recommendation'] ?? '';
  return content.replace(/```json[\s\S]*?```/, '').trim();
});

function toggleReasoning(key: string) {
  if (expandedReasonings.value.has(key)) {
    expandedReasonings.value.delete(key);
  } else {
    expandedReasonings.value.add(key);
  }
}

function scoreClass(score: number | undefined): string {
  if (score === undefined) return '';
  if (score >= 8) return 'score-green';
  if (score >= 5) return 'score-amber';
  return 'score-red';
}

function elapsed(run: { startTime: number; endTime: number | null }): string {
  const end = run.endTime ?? now.value;
  const ms = end - run.startTime;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function formatDuration(ms: number | undefined): string {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function formatNumber(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString();
}

function cacheSavings(costs: any): string {
  if (!costs?.cachedTokens || !costs?.cost) return '—';
  // Estimate: cached tokens save ~90% of input cost
  const fullInputCost =
    costs.inputTokens > 0 ? costs.cost * (costs.inputTokens / (costs.inputTokens - costs.cachedTokens * 0.9)) : 0;
  const savings = fullInputCost - costs.cost;
  if (savings <= 0) return '—';
  return `$${savings.toFixed(2)}`;
}

function formatOutputName(name: string): string {
  return name
    .replace(/^[^_]+_/, '') // strip framework prefix
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function renderMarkdown(content: string): string {
  if (!content) return '';
  return marked(content) as string;
}
</script>

<style scoped>
.benchmark-page {
  min-height: 100vh;
  background: var(--color-bg-secondary);
}

.bench-sidebar {
  position: fixed;
  top: 4rem;
  left: 0;
  width: 200px;
  padding: 0 0 2rem 1.5rem;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
}

.bench-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bench-nav-group-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
  padding: 0.75rem 0.625rem 0.25rem;
}

.bench-nav-item {
  display: block;
  padding: 0.4rem 0.625rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bench-nav-item:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
}

.bench-nav-item.active {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
  font-weight: 500;
}

.bench-nav-child {
  padding-left: 1.25rem;
  font-size: 0.8125rem;
}

.benchmark-content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
}

.benchmark-header {
  margin-bottom: 2.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.cancel-btn {
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: transparent;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.cancel-btn:hover {
  color: var(--color-text-primary);
  border-color: var(--color-text-secondary);
}

.benchmark-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 0.25rem;
}

.benchmark-subtitle {
  color: var(--color-text-secondary);
  font-size: 0.9375rem;
}

.benchmark-section {
  background: #fff;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius);
  padding: 1.75rem;
  margin-bottom: 1.5rem;
}

.section-heading {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--color-text-primary);
}

.section-note {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: 1.25rem;
}

/* Progress cards */
.progress-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.progress-card {
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius);
  padding: 1rem;
  transition: border-color var(--transition-base);
}

.progress-card.complete {
  border-color: var(--color-success);
}
.progress-card.error {
  border-color: var(--color-danger);
}
.progress-card.processing {
  border-color: var(--color-accent);
}

.progress-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
}

.model-status-icon {
  font-size: 1rem;
}
.progress-card.complete .model-status-icon {
  color: var(--color-success);
}
.progress-card.error .model-status-icon {
  color: var(--color-danger);
}

.progress-bar-track {
  height: 4px;
  background: var(--color-bg-tertiary);
  border-radius: 2px;
  margin-bottom: 0.5rem;
}

.progress-bar-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: 2px;
  transition: width 0.5s ease;
}

.progress-step-label,
.progress-pending,
.progress-error {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.progress-error {
  color: var(--color-danger);
}

.progress-card-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.evaluation-status {
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius-sm);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
}

.eval-error {
  color: var(--color-danger);
}

/* Performance table */
.table-wrapper {
  overflow-x: auto;
}

.perf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.perf-table th {
  text-align: left;
  padding: 0.625rem 0.875rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-primary);
  white-space: nowrap;
}

.perf-table td {
  padding: 0.75rem 0.875rem;
  border-bottom: 1px solid var(--color-border-primary);
}

.perf-table tr:last-child td {
  border-bottom: none;
}
.model-col {
  font-weight: 500;
}

/* Quality scores */
.scores-block {
  margin-bottom: 2rem;
}

.scores-heading {
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--color-text-secondary);
}

.scores-grid {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.score-cell {
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-sm);
  padding: 1rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
}

.score-cell:hover {
  border-color: var(--color-border-secondary);
}
.score-cell.score-green {
  border-left: 3px solid var(--color-success);
}
.score-cell.score-amber {
  border-left: 3px solid var(--color-warning);
}
.score-cell.score-red {
  border-left: 3px solid var(--color-danger);
}

.score-number {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.score-cell.score-green .score-number {
  color: var(--color-success);
}
.score-cell.score-amber .score-number {
  color: var(--color-warning);
}
.score-cell.score-red .score-number {
  color: var(--color-danger);
}

.score-model-label {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
}

.score-reasoning {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border-primary);
}

.score-dimensions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.score-dim-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8125rem;
}

.dim-name {
  color: var(--color-text-secondary);
  text-transform: capitalize;
}
.dim-val {
  font-weight: 600;
}
.dim-val.score-green {
  color: var(--color-success);
}
.dim-val.score-amber {
  color: var(--color-warning);
}
.dim-val.score-red {
  color: var(--color-danger);
}

.scores-summary {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  font-style: italic;
  margin-top: 0.5rem;
}

.scores-na {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  font-style: italic;
}

/* Human ratings */
.human-rating-block {
  margin-bottom: 1.5rem;
}

.human-rating-model {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.human-rating-row {
  display: grid;
  grid-template-columns: 160px auto 1fr;
  gap: 0.75rem;
  align-items: start;
  margin-bottom: 0.75rem;
}

.human-rating-label {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  padding-top: 0.25rem;
}

.star-rating {
  display: flex;
  gap: 2px;
}

.star-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: var(--color-border-secondary);
  padding: 0;
  transition: color var(--transition-fast);
}

.star-btn.filled,
.star-btn:hover {
  color: var(--color-accent-yellow);
}

.rating-notes {
  width: 100%;
  padding: 0.375rem 0.625rem;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-sm);
  font-size: 0.8125rem;
  font-family: inherit;
  resize: vertical;
  min-height: 2.5rem;
  color: var(--color-text-primary);
}

.rating-notes:focus {
  outline: none;
  border-color: var(--color-accent);
}

.export-btn {
  background: none;
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--border-radius-sm);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  transition: all var(--transition-fast);
}

.export-btn:hover {
  border-color: var(--color-text-primary);
  color: var(--color-text-primary);
}

/* Model output tabs */
.tab-bar {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-border-primary);
  padding-bottom: 0;
}

.tab-btn {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-family: inherit;
  margin-bottom: -1px;
  transition: all var(--transition-fast);
}

.tab-btn:hover {
  color: var(--color-text-primary);
}
.tab-btn.active {
  color: var(--color-text-primary);
  border-bottom-color: var(--color-text-primary);
  font-weight: 500;
}

.outputs-heading {
  margin-bottom: 1rem;
}

.output-block-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--color-text-secondary);
}

.output-empty {
  color: var(--color-text-muted);
  font-style: italic;
  font-size: 0.9rem;
}

/* Prose rendering */
.prose-content {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--color-text-primary);
}

.prose-content :deep(h1),
.prose-content :deep(h2),
.prose-content :deep(h3) {
  font-weight: 600;
  margin: 1.5rem 0 0.75rem;
  line-height: 1.3;
}

.prose-content :deep(h1) {
  font-size: 1.375rem;
}
.prose-content :deep(h2) {
  font-size: 1.125rem;
}
.prose-content :deep(h3) {
  font-size: 1rem;
}
.prose-content :deep(p) {
  margin-bottom: 0.875rem;
}
.prose-content :deep(ul),
.prose-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.875rem;
}
.prose-content :deep(li) {
  margin-bottom: 0.25rem;
}
.prose-content :deep(strong) {
  font-weight: 600;
}

/* Recommendation */
.ranking-summary {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.recommended-badge {
  background: rgba(26, 127, 55, 0.1);
  color: var(--color-success);
  border: 1px solid rgba(26, 127, 55, 0.2);
  border-radius: 2rem;
  padding: 0.375rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.avoid-badge {
  background: rgba(207, 34, 46, 0.08);
  color: var(--color-danger);
  border: 1px solid rgba(207, 34, 46, 0.15);
  border-radius: 2rem;
  padding: 0.375rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--color-border-primary);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}

.eval-running .spinner {
  width: 1.125rem;
  height: 1.125rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
